import type { BeadDesign } from '../types';

const dbName = 'bead-studio-ai';
const dbVersion = 1;
const designsStore = 'designs';
const metaStore = 'meta';
const draftKey = 'current-draft';

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, dbVersion);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(designsStore)) {
        db.createObjectStore(designsStore, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(metaStore)) {
        db.createObjectStore(metaStore);
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transaction<T>(storeName: string, mode: IDBTransactionMode, action: (store: IDBObjectStore) => IDBRequest<T>) {
  return openDatabase().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(storeName, mode);
        const store = tx.objectStore(storeName);
        const request = action(store);

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
        tx.oncomplete = () => db.close();
        tx.onerror = () => {
          db.close();
          reject(tx.error);
        };
      })
  );
}

export function saveDraft(design: BeadDesign) {
  return transaction(metaStore, 'readwrite', (store) => store.put(design, draftKey));
}

export function loadDraft() {
  return transaction<BeadDesign | undefined>(metaStore, 'readonly', (store) => store.get(draftKey));
}

export function clearDraft() {
  return transaction(metaStore, 'readwrite', (store) => store.delete(draftKey));
}

export function saveDesign(design: BeadDesign) {
  return transaction(designsStore, 'readwrite', (store) => store.put(design));
}

export function loadDesign(id: string) {
  return transaction<BeadDesign | undefined>(designsStore, 'readonly', (store) => store.get(id));
}

export function deleteDesign(id: string) {
  return transaction(designsStore, 'readwrite', (store) => store.delete(id));
}

export function listDesigns(): Promise<BeadDesign[]> {
  return transaction<BeadDesign[]>(designsStore, 'readonly', (store) => store.getAll()).then((designs) =>
    designs.sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
  );
}
